import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Activity, 
  Sun, 
  PersonStanding, 
  MapPin, 
  Phone, 
  Clock,
  Mail,
  CheckCircle2, 
  ChevronRight
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReviewWidget from "@/components/ReviewWidget";

import { SEOHead } from "@/components/SEOHead";
import { useState, useEffect } from "react";

export default function Index() {
  const [loadMap, setLoadMap] = useState(false);

  useEffect(() => {
    const handleInteraction = () => {
      setLoadMap(true);
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };

    window.addEventListener('scroll', handleInteraction, { passive: true });
    window.addEventListener('mousemove', handleInteraction, { passive: true });
    window.addEventListener('touchstart', handleInteraction, { passive: true });
    window.addEventListener('keydown', handleInteraction, { passive: true });

    const timer = setTimeout(handleInteraction, 3500);

    return () => {
      cleanup();
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Woods Spine & Wellness | Advanced Therapies in Longview, TX"
        description="Specializing in SoftWave TRT, Red Light Therapy, and Spinal Decompression to meet your health and wellness needs at Woods Spine and Wellness in Longview, TX."
        ogImage="https://vibe.filesafe.space/1774569058151009111/assets/58600117-1663-43e4-98fd-7b421e93ecd3.png"
        canonical="https://woodsspine.com/"
      />
      <Navbar />
      <main id="main-content" className="flex-grow">
        {/* Hero Section */}
        <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-40 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/58600117-1663-43e4-98fd-7b421e93ecd3.png&w=1920&output=webp" 
              alt="Modern spine and wellness clinic" 
              width="1920"
              height="1080"
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-black/60 lg:bg-gradient-to-r lg:from-black/80 lg:via-black/60 lg:to-transparent"></div>
          </div>
          
          <div className="container relative z-10 mx-auto px-4">
            <div className="max-w-2xl text-white">
              <Link to="/appointment-scheduling" className="inline-flex items-center rounded-full border border-white/30 bg-white/10 px-3 py-1 text-sm font-medium backdrop-blur-sm mb-6 hover:bg-white/20 transition-colors cursor-pointer">
                <span className="flex h-2 w-2 rounded-full bg-accent mr-2"></span>
                Accepting New Patients in Longview, TX
              </Link>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 [text-shadow:_0_1px_3px_rgba(0,0,0,0.5)]">
                Woods Spine &<br/>
                <span className="text-accent">Wellness.</span>
              </h1>
              <p className="text-2xl md:text-3xl font-medium text-white mb-6 [text-shadow:_0_1px_2px_rgba(0,0,0,0.5)]">
                Get back to a better you.
              </p>
              <p className="text-lg md:text-xl mb-8 text-white/90 max-w-xl [text-shadow:_0_1px_2px_rgba(0,0,0,0.5)]">
                Advanced therapies including <Link to="/longview-softwave-pain-management-tissue-regeneration" className="text-accent hover:underline transition-colors">SoftWave TRT</Link>, <Link to="/longview-red-light-therapy" className="text-accent hover:underline transition-colors">Red Light Therapy</Link>, <Link to="/longview-spinal-decompression" className="text-accent hover:underline transition-colors">DRX9000 Spinal Decompression</Link>, and <Link to="/longview-chirothin-weight-loss" className="text-accent hover:underline transition-colors">ChiroThin Weight Loss</Link> to meet your health and wellness needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-accent text-white hover:bg-accent/90 border-0" asChild>
                  <Link to="/appointment-scheduling">
                    Book an Appointment <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white" asChild>
                  <Link to="/specials">View New Patient Specials</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-24 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Advanced Therapies</h2>
              <p className="text-lg text-muted-foreground">
                We offer state-of-the-art non-invasive treatments to help you recover faster, reduce pain, and improve your overall quality of life.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Service 1: ChiroThin */}
              <Link to="/longview-chirothin-weight-loss" className="block group">
                <Card className="overflow-hidden border-0 shadow-lg h-full transition-shadow hover:shadow-xl">
                  <div className="h-48 overflow-hidden bg-white p-4 flex items-center justify-center">
                    <img 
                      src="https://wsrv.nl/?url=https://chirothin.com/logo.png&w=600&output=webp&q=80" 
                      alt="ChiroThin Weight Loss" 
                      width="400"
                      height="300"
                      loading="lazy"
                      decoding="async"
                      className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <CardHeader>
                    <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Activity className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">ChiroThin</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      A doctor-supervised weight loss program designed to help you achieve dramatic, sustainable results safely and effectively.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start text-sm"><CheckCircle2 className="h-4 w-4 text-accent mr-2 mt-0.5" aria-hidden="true" /> Doctor Supervised</li>
                      <li className="flex items-start text-sm"><CheckCircle2 className="h-4 w-4 text-accent mr-2 mt-0.5" aria-hidden="true" /> Sustainable Results</li>
                    </ul>
                    <div className="flex items-center text-sm font-medium text-accent mt-4 group-hover:text-accent/80 transition-colors">
                      Learn more <span className="sr-only">about ChiroThin Weight Loss</span> <ChevronRight className="h-4 w-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>

              {/* Service 2: DRX9000 */}
              <Link to="/longview-spinal-decompression" className="block group">
                <Card className="overflow-hidden border-0 shadow-lg h-full transition-shadow hover:shadow-xl">
                  <div className="h-48 overflow-hidden bg-white flex items-center justify-center">
                    <img 
                      src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/2d377a90-a463-4e54-a3e0-3b713a3a163d.png&w=600&output=webp&q=80" 
                      alt="DRX9000 Spinal Decompression Device" 
                      width="400"
                      height="300"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <CardHeader>
                    <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <PersonStanding className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">DRX9000 Decompression</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      State-of-the-art non-surgical spinal decompression therapy designed to relieve pressure on structures that may be causing back pain.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start text-sm"><CheckCircle2 className="h-4 w-4 text-accent mr-2 mt-0.5" aria-hidden="true" /> Treats herniated discs</li>
                      <li className="flex items-start text-sm"><CheckCircle2 className="h-4 w-4 text-accent mr-2 mt-0.5" aria-hidden="true" /> Relieves sciatica</li>
                    </ul>
                    <div className="flex items-center text-sm font-medium text-accent mt-4 group-hover:text-accent/80 transition-colors">
                      Learn more <span className="sr-only">about DRX9000 Spinal Decompression</span> <ChevronRight className="h-4 w-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>

              {/* Service 3: Red Light */}
              <Link to="/longview-red-light-therapy" className="block group">
                <Card className="overflow-hidden border-0 shadow-lg h-full transition-shadow hover:shadow-xl">
                  <div className="h-48 overflow-hidden bg-black flex items-center justify-center">
                    <img 
                      src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/attachments/e3fefda6-4541-41e2-b1b1-3631721c72b4.png&w=600&output=webp&q=80" 
                      alt="Dahlia Red Light Therapy Bed" 
                      width="400"
                      height="300"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 opacity-90"
                    />
                  </div>
                  <CardHeader>
                    <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Sun className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">Red Light Therapy</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Utilizes specific wavelengths of light to penetrate deep into tissues, boosting cellular energy, reducing pain, and improving skin health.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start text-sm"><CheckCircle2 className="h-4 w-4 text-accent mr-2 mt-0.5" aria-hidden="true" /> Reduces inflammation</li>
                      <li className="flex items-start text-sm"><CheckCircle2 className="h-4 w-4 text-accent mr-2 mt-0.5" aria-hidden="true" /> Boosts circulation</li>
                    </ul>
                    <div className="flex items-center text-sm font-medium text-accent mt-4 group-hover:text-accent/80 transition-colors">
                      Learn more <span className="sr-only">about Red Light Therapy</span> <ChevronRight className="h-4 w-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>

              {/* Service 4: SoftWave */}
              <Link to="/longview-softwave-pain-management-tissue-regeneration" className="block group">
                <Card className="overflow-hidden border-0 shadow-lg h-full transition-shadow hover:shadow-xl">
                  <div className="h-48 overflow-hidden bg-white p-4 flex items-center justify-center">
                    <img 
                      src="https://wsrv.nl/?url=https://softwavetrt.com/wp-content/uploads/2026/02/softwave099-1.png&w=600&output=webp&q=80" 
                      alt="SoftWave TRT Device" 
                      width="400"
                      height="300"
                      loading="lazy"
                      decoding="async"
                      className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <CardHeader>
                    <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Activity className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">SoftWave TRT</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Advanced shockwave therapy that stimulates your body's natural healing process, reduces inflammation, and promotes cellular repair.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start text-sm"><CheckCircle2 className="h-4 w-4 text-accent mr-2 mt-0.5" aria-hidden="true" /> Non-invasive pain relief</li>
                      <li className="flex items-start text-sm"><CheckCircle2 className="h-4 w-4 text-accent mr-2 mt-0.5" aria-hidden="true" /> Accelerates healing</li>
                    </ul>
                    <div className="flex items-center text-sm font-medium text-accent mt-4 group-hover:text-accent/80 transition-colors">
                      Learn more <span className="sr-only">about SoftWave TRT</span> <ChevronRight className="h-4 w-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-24">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Meet Your Wellness Team</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  At Woods Spine & Wellness, <Link to="/about" className="text-accent hover:underline underline-offset-4 font-medium transition-colors">Dr. Tucker Woods</Link> and <Link to="/about" className="text-accent hover:underline underline-offset-4 font-medium transition-colors">Dr. Lance Potter</Link> are dedicated to helping you achieve optimal health through personalized, non-invasive treatments.
                </p>
                <p className="text-muted-foreground mb-8">
                  Our clinic in Longview, TX is equipped with the latest technology to ensure you receive the highest standard of care. We believe in treating the root cause of your pain, not just the symptoms, so you can get back to living your life to the fullest. <Link to="/about" className="text-accent hover:underline font-medium inline-flex items-center mt-2 transition-colors">Learn more about our practice <ChevronRight className="h-4 w-4 ml-1" /></Link>
                </p>
                
                <div className="grid grid-cols-2 gap-6">
                  <div className="border-l-4 border-accent pl-4">
                    <div className="text-2xl font-bold text-primary mb-1">15+</div>
                    <p className="text-sm text-muted-foreground">Years Experience</p>
                  </div>
                  <div className="border-l-4 border-accent pl-4">
                    <div className="text-2xl font-bold text-primary mb-1">5k+</div>
                    <p className="text-sm text-muted-foreground">Patients Treated</p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="grid grid-cols-2 gap-4 sm:gap-6">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-xl">
                    <img 
                      src="https://wsrv.nl/?url=https://assets.cdn.filesafe.space/7UxsnzcuJDZvyZp0wk8z/media/69c6d2042e89d8804a561325.jpeg&w=600&output=webp&q=80"
                      alt="Dr. Tucker Woods" 
                      width="400"
                      height="533"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-xl">
                    <img 
                      src="https://wsrv.nl/?url=https://assets.cdn.filesafe.space/7UxsnzcuJDZvyZp0wk8z/media/6615a6bdb198161b5eb9dc63.jpeg&w=600&output=webp&q=80" 
                      alt="Dr. Lance Potter"
                      width="400"
                      height="533"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 md:translate-x-0 md:-left-6 bg-card p-6 rounded-xl shadow-xl max-w-xs w-[90%] md:w-auto border border-border z-10">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="flex -space-x-3">
                      <div className="w-10 h-10 rounded-full bg-secondary border-2 border-background flex items-center justify-center text-xs font-bold text-secondary-foreground">TW</div>
                      <div className="w-10 h-10 rounded-full bg-accent border-2 border-background flex items-center justify-center text-xs font-bold text-accent-foreground">LP</div>
                    </div>
                    <div className="text-sm font-bold">Expert Care</div>
                  </div>
                  <p className="text-xs text-muted-foreground">Dedicated to your long-term health and mobility.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section (AIO Optimized) */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-muted-foreground">Clear answers to help you understand our therapies and approach to wellness.</p>
            </div>
            <div className="space-y-8">
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">What is DRX9000 Spinal Decompression and how does it work?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  The <Link to="/longview-spinal-decompression" className="text-accent hover:underline underline-offset-2 transition-colors">DRX9000</Link> is an advanced, non-surgical spinal decompression system. It works by gently stretching the spine to relieve pressure on compressed discs and nerves. This process creates a vacuum effect that draws herniated or bulging disc material back into place, promoting natural healing for conditions like sciatica and lower back pain.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Is SoftWave TRT painful?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  <Link to="/longview-softwave-pain-management-tissue-regeneration" className="text-accent hover:underline underline-offset-2 transition-colors">SoftWave Tissue Regeneration Technology (TRT)</Link> is a non-invasive, generally painless procedure. Most patients feel only a mild pulsing sensation during treatment. It uses supersonic sound waves to stimulate your body's natural stem cell production, reducing inflammation and accelerating tissue repair without surgery or medication.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">How long does a Red Light Therapy session take?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  A typical Dahlia <Link to="/longview-red-light-therapy" className="text-accent hover:underline underline-offset-2 transition-colors">Red Light Therapy</Link> session takes just 20 to 30 minutes. It is a quick, painless, and relaxing treatment that penetrates deep into tissues to boost cellular energy, reduce inflammation, and aid in body contouring and fat reduction.
                </p>
              </div>
              <div className="pb-2">
                <h3 className="text-xl font-bold text-primary mb-3">Are you accepting new patients in Longview, TX?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Yes, Woods Spine & Wellness is currently accepting new patients at our Longview, TX clinic. Dr. Tucker Woods and Dr. Lance Potter are ready to provide comprehensive, personalized care for your spine and wellness needs.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <ReviewWidget />

        {/* Contact & Booking Section */}
        <section id="contact" className="py-24 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-16">
              
              {/* Contact Info */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Feel Better?</h2>
                <p className="text-primary-foreground/80 mb-10 text-lg">
                  Schedule your consultation today. Our team is ready to develop a personalized treatment plan for your specific needs.
                </p>
                
                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="bg-white/10 p-3 rounded-lg">
                      <MapPin className="h-6 w-6 text-accent" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-1">Visit Us</h3>
                      <a href="https://www.google.com/maps?cid=8246813296068272509" target="_blank" rel="noopener noreferrer" className="text-primary-foreground/70 hover:text-white transition-colors block">
                        530 E LOOP 281 STE 103<br/>Longview, TX 75605
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-white/10 p-3 rounded-lg">
                      <Phone className="h-6 w-6 text-accent" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-1">Call Us</h3>
                      <a href="tel:+19032158828" className="text-primary-foreground/70 hover:text-white transition-colors block">
                        (903) 215-8828
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-white/10 p-3 rounded-lg">
                      <Mail className="h-6 w-6 text-accent" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-1">Email Us</h3>
                      <a href="mailto:woodsspine.office@gmail.com" className="text-primary-foreground/70 hover:text-white transition-colors block">
                        woodsspine.office@gmail.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-white/10 p-3 rounded-lg">
                      <Clock className="h-6 w-6 text-accent" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-1">Hours</h3>
                      <ul className="text-primary-foreground/70 text-sm space-y-1">
                        <li>Mon-Wed: 8:30 AM - 12:30 PM, 2:00 PM - 6:00 PM</li>
                        <li>Thu: 8:30 AM - 12:30 PM, 2:00 PM - 5:00 PM</li>
                        <li>Fri-Sun: Closed</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div id="booking" className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10 bg-muted relative">
                {!loadMap ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
                    <MapPin className="h-10 w-10 mb-2 opacity-50 animate-pulse" />
                    <p>Loading map...</p>
                  </div>
                ) : (
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3363.500840582648!2d-94.7292483!3d32.5394761!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x86363fe9b171deb3%3A0x72729ff23a5ca37d!2sWoods%20Spine%20%26%20Wellness!5e0!3m2!1sen!2sus!4v1774571953004!5m2!1sen!2sus" 
                    title="Google Maps Location"
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }} 
                    allowFullScreen 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    className="absolute inset-0"
                  ></iframe>
                )}
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
